import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { APIProvider, Map as GoogleMap, useMap } from "@vis.gl/react-google-maps";
import { GoogleMapsOverlay } from "@deck.gl/google-maps";
import { IconLayer } from "@deck.gl/layers";
import { fetchVehicles } from "@/lib/api";
import type { Vehicle } from "@/types";
import { resolveVehicleModel, resolveVehicleColor, vehicleIconDataUri } from "@/lib/vehicle-icons";
import { resolveGoogleMapId, hasCustomGoogleMapId, GOOGLE_MAPS_CLEAN_STYLE } from "@/lib/google-maps-style";

// Piloto isolado (Opção B do plano de arquitetura, 2026-09-03): Google Maps
// real (mesma API/key já usada em route-history-center.tsx) + deck.gl
// IconLayer (GoogleMapsOverlay/WebGLOverlayView) pros veículos, em vez de
// react-leaflet + L.divIcon. Objetivo ÚNICO desta rota: medir FPS/DOM/heap
// com a frota real (mesmo harness Puppeteer da sessão de diagnóstico) e
// confirmar clique funcionando — NÃO é a versão final (painel lateral rico,
// geocercas, trilhas, filtros/busca continuam só no mapa Leaflet real por
// enquanto, ver plano). Fora do menu de propósito.
export const Route = createFileRoute("/dev/google-deckgl-map")({
  component: DevGoogleDeckglMapPage,
});

// Mesmo fallback de src/routes/index.tsx (detectVehicleType) — v.type nem
// sempre vem preenchido do Traccar, sniffar pelo nome cobre o resto.
function detectVehicleType(v: Vehicle): "car" | "moto" | "truck" | "van" | "machine" {
  if (v.type) return v.type;
  const n = v.name.toLowerCase();
  if (/moto|scooter|honda cg|xre|biz|pop|factor|fazer|cb ?\d/.test(n)) return "moto";
  if (/caminh(ã|a)o|truck|volvo|scania|iveco|actros|axor|constellation|fh\b|vm\b/.test(n)) return "truck";
  if (/van|sprinter|ducato|master|daily|transit|hr\b|jumper/.test(n)) return "van";
  if (/m(á|a)quina|trator|escavadeira|retro|p(á|a) carreg|bulldozer|colhe|jcb|cat\b/.test(n)) return "machine";
  return "car";
}

// PNGs gerados por scripts/render-vehicle-icons são 1024x1024 (qualidade do
// contorno 3D na geração, ver comentário em scene.ts) — reaproveitados como
// estão neste piloto. ICON_PX é o tamanho de EXIBIÇÃO no mapa (mesmo padrão
// visual do marcador Leaflet atual, ~46px); deck.gl faz o downscale na GPU
// e cacheia a textura por URL — mesma imagem nunca é recarregada duas vezes
// pra veículos com o mesmo modelo+cor.
const ICON_SRC_PX = 1024;
const ICON_DISPLAY_PX = 44;

function iconUrlFor(v: Vehicle): string {
  const model = resolveVehicleModel(v.iconKey, detectVehicleType(v));
  const color = resolveVehicleColor(v);
  return vehicleIconDataUri(model, color);
}

// Componente-ponte: GoogleMapsOverlay não é um layer React — precisa da
// instância real de google.maps.Map (via useMap(), do @vis.gl/react-google-maps)
// pra se acoplar. Cria o overlay uma vez (mount) e só troca os `layers` do
// deck.gl (setProps) toda vez que a frota muda — equivalente ao
// source.setData() do MapLibre/Traccar, só que na API do deck.gl.
function DeckGlVehicleLayer({
  vehicles,
  onSelect,
}: {
  vehicles: Vehicle[];
  onSelect: (v: Vehicle) => void;
}) {
  const map = useMap();
  const overlayRef = useRef<GoogleMapsOverlay | null>(null);
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  useEffect(() => {
    if (!map) return;
    const overlay = new GoogleMapsOverlay({});
    overlay.setMap(map);
    overlayRef.current = overlay;
    return () => {
      overlay.setMap(null);
      overlayRef.current = null;
    };
  }, [map]);

  useEffect(() => {
    const overlay = overlayRef.current;
    if (!overlay) return;
    overlay.setProps({
      layers: [
        new IconLayer<Vehicle>({
          id: "vehicles",
          data: vehicles,
          pickable: true,
          sizeUnits: "pixels",
          getPosition: (v) => [v.lng, v.lat],
          getSize: ICON_DISPLAY_PX,
          // Sentido/offset de rotação não confirmado visualmente ainda —
          // course do Traccar é graus a partir do norte, sentido horário;
          // ajustar sinal aqui depois do primeiro teste visual real.
          getAngle: (v) => 360 - (v.bearing ?? 0),
          getIcon: (v) => ({
            url: iconUrlFor(v),
            width: ICON_SRC_PX,
            height: ICON_SRC_PX,
            anchorY: ICON_SRC_PX / 2,
          }),
          onClick: (info) => {
            if (info.object) onSelectRef.current(info.object as Vehicle);
          },
        }),
      ],
    });
  }, [vehicles]);

  return null;
}

function DevGoogleDeckglMapPage() {
  const apiKey = (import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined) ?? "";
  // Query key própria (não ["vehicles"]) — não compartilha cache com o mapa
  // real, pra não interferir em nada fora desta rota isolada.
  const { data: vehicles = [] } = useQuery({
    queryKey: ["vehicles-deckgl-pilot"],
    queryFn: fetchVehicles,
    refetchInterval: 15_000,
    retry: false,
  });
  const [selected, setSelected] = useState<Vehicle | null>(null);

  if (!apiKey) {
    return (
      <div className="flex h-screen items-center justify-center p-6 text-center text-muted-foreground">
        Configure VITE_GOOGLE_MAPS_API_KEY para carregar este piloto.
      </div>
    );
  }

  const center = vehicles.length
    ? { lat: vehicles[0].lat, lng: vehicles[0].lng }
    : { lat: -14.2, lng: -51.9 };

  return (
    <div className="relative h-screen w-full">
      <APIProvider apiKey={apiKey}>
        <GoogleMap
          mapId={resolveGoogleMapId()}
          styles={hasCustomGoogleMapId() ? undefined : GOOGLE_MAPS_CLEAN_STYLE}
          defaultCenter={center}
          defaultZoom={5}
          disableDefaultUI
          zoomControl
          gestureHandling="greedy"
        >
          <DeckGlVehicleLayer vehicles={vehicles} onSelect={setSelected} />
        </GoogleMap>
      </APIProvider>

      <div className="pointer-events-none absolute left-4 top-4 z-10 rounded-lg border border-border bg-card/95 px-3 py-2 text-sm shadow-lg backdrop-blur">
        <div className="font-semibold">Piloto: Google Maps + deck.gl (Opção B)</div>
        <div className="text-muted-foreground">{vehicles.length} veículos carregados</div>
      </div>

      {selected && (
        <div className="absolute right-4 top-4 z-10 w-72 rounded-lg border border-border bg-card p-4 shadow-lg">
          <button
            type="button"
            className="float-right text-muted-foreground hover:text-foreground"
            onClick={() => setSelected(null)}
            aria-label="Fechar"
          >
            ×
          </button>
          <div className="font-semibold">{selected.name}</div>
          <div className="text-sm text-muted-foreground">{selected.plate}</div>
          <div className="mt-2 text-sm">Status: {selected.status}</div>
          <div className="text-sm">Velocidade: {Math.round(selected.speed ?? 0)} km/h</div>
        </div>
      )}
    </div>
  );
}
